import React, { useMemo, useRef, useState, useCallback } from "react";
import { ZoomIn, ZoomOut, Waves } from "lucide-react";
import BaldachinCard from "@/components/BaldachinCard";
import { BEACH_DECORATIONS } from "@/lib/beachDecorations";

/**
 * Renders baldachins on an absolutely-positioned canvas.
 * Props:
 *  - baldachins: array with x,y,w,h
 *  - mode: card mode
 *  - onCardClick(b)
 *  - selectedId
 *  - dimIds: Set of ids to dim (filtered out)
 *  - editable: enable drag to move
 *  - onMove(id, x, y)
 *  - initialZoom
 */
const DRAG_THRESHOLD = 5;

export default function BeachMap({
  baldachins,
  mode = "detailed",
  onCardClick,
  selectedId,
  dimIds,
  editable = false,
  onMove,
  initialZoom = 0.85,
  minHeight = 420,
  showDecorations = true,
}) {
  const [zoom, setZoom] = useState(initialZoom);
  const containerRef = useRef(null);
  const dragRef = useRef(null);

  const bounds = useMemo(() => {
    let maxX = 400;
    let maxY = 300;
    baldachins.forEach((b) => {
      maxX = Math.max(maxX, b.x + b.w);
      maxY = Math.max(maxY, b.y + b.h);
    });
    if (showDecorations) {
      BEACH_DECORATIONS.arrows.forEach((a) => {
        maxX = Math.max(maxX, a.x1, a.x2);
        maxY = Math.max(maxY, a.y1, a.y2);
      });
      BEACH_DECORATIONS.labels.forEach((l) => {
        maxX = Math.max(maxX, l.x + l.w);
        maxY = Math.max(maxY, l.y + l.h);
      });
    }
    return { w: maxX + 80, h: maxY + 80 };
  }, [baldachins, showDecorations]);

  const handlePointerDown = useCallback(
    (e, b) => {
      if (!editable) return;
      e.preventDefault();
      const rect = containerRef.current.getBoundingClientRect();
      dragRef.current = {
        id: b.id,
        b,
        startX: e.clientX,
        startY: e.clientY,
        offsetX: (e.clientX - rect.left + containerRef.current.scrollLeft) / zoom - b.x,
        offsetY: (e.clientY - rect.top + containerRef.current.scrollTop) / zoom - b.y,
        moved: false,
      };
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    [editable, zoom]
  );

  const handlePointerMove = useCallback(
    (e) => {
      if (!editable || !dragRef.current) return;
      const d = dragRef.current;
      if (!d.moved) {
        const dist = Math.hypot(e.clientX - d.startX, e.clientY - d.startY);
        if (dist < DRAG_THRESHOLD) return;
        d.moved = true;
      }
      const rect = containerRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left + containerRef.current.scrollLeft) / zoom - d.offsetX;
      const y = (e.clientY - rect.top + containerRef.current.scrollTop) / zoom - d.offsetY;
      onMove?.(d.id, Math.max(0, Math.round(x)), Math.max(0, Math.round(y)));
    },
    [editable, zoom, onMove]
  );

  const handlePointerUp = useCallback(() => {
    const d = dragRef.current;
    dragRef.current = null;
    if (editable && d && !d.moved) {
      onCardClick?.(d.b);
    }
  }, [editable, onCardClick]);

  return (
    <div className="relative min-w-0">
      <div className="absolute right-3 top-3 z-20 flex flex-col gap-1.5">
        <button
          type="button"
          data-testid="map-zoom-in"
          onClick={() => setZoom((z) => Math.min(1.6, +(z + 0.12).toFixed(2)))}
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-stone-200 bg-white/90 text-stone-700 shadow-sm backdrop-blur transition hover:bg-white"
        >
          <ZoomIn className="h-4 w-4" />
        </button>
        <button
          type="button"
          data-testid="map-zoom-out"
          onClick={() => setZoom((z) => Math.max(0.35, +(z - 0.12).toFixed(2)))}
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-stone-200 bg-white/90 text-stone-700 shadow-sm backdrop-blur transition hover:bg-white"
        >
          <ZoomOut className="h-4 w-4" />
        </button>
      </div>

      <div
        ref={containerRef}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        className="bb-scroll relative w-full max-w-full overflow-auto rounded-2xl border border-stone-200 bg-gradient-to-b from-sky-50 to-stone-50"
        style={{ height: Math.max(minHeight, 0), touchAction: editable ? "none" : "auto" }}
        data-testid="beach-map"
      >
        <div
          className="relative"
          style={{ width: bounds.w * zoom, height: bounds.h * zoom, minWidth: "100%" }}
        >
          <div className="pointer-events-none absolute left-4 top-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-sky-400/80">
            <Waves className="h-4 w-4" /> Water
          </div>

          {showDecorations && (
            <>
              <svg
                className="pointer-events-none absolute left-0 top-0"
                width={bounds.w * zoom}
                height={bounds.h * zoom}
                viewBox={`0 0 ${bounds.w} ${bounds.h}`}
                aria-hidden="true"
              >
                <defs>
                  <marker
                    id="bb-arrowhead"
                    markerUnits="userSpaceOnUse"
                    markerWidth="40"
                    markerHeight="30"
                    refX="34"
                    refY="15"
                    orient="auto"
                  >
                    <path d="M0,0 L34,15 L0,30 Z" fill="#57534e" fillOpacity="0.6" />
                  </marker>
                </defs>
                {BEACH_DECORATIONS.arrows.map((a) => (
                  <line
                    key={a.id}
                    data-testid={`map-arrow-${a.id}`}
                    x1={a.x1}
                    y1={a.y1}
                    x2={a.x2}
                    y2={a.y2}
                    stroke="#57534e"
                    strokeOpacity="0.5"
                    strokeWidth="8"
                    strokeLinecap="round"
                    markerEnd="url(#bb-arrowhead)"
                  />
                ))}
              </svg>
              {BEACH_DECORATIONS.labels.map((l) => (
                <div
                  key={l.id}
                  data-testid={`map-label-${l.id}`}
                  className="pointer-events-none absolute flex items-center justify-center rounded-xl border-2 border-dashed border-stone-300 bg-white/75 font-heading font-extrabold uppercase tracking-wide text-stone-600 shadow-sm backdrop-blur-sm"
                  style={{
                    left: l.x * zoom,
                    top: l.y * zoom,
                    width: l.w * zoom,
                    height: l.h * zoom,
                    fontSize: Math.max(10, l.w * zoom * 0.11),
                  }}
                >
                  {l.text}
                </div>
              ))}
            </>
          )}

          {baldachins.map((b) => (
            <div
              key={b.id}
              onPointerDown={(e) => handlePointerDown(e, b)}
              style={{
                position: "absolute",
                left: b.x * zoom,
                top: b.y * zoom,
                width: b.w * zoom,
                height: b.h * zoom,
                cursor: editable ? "grab" : "pointer",
              }}
            >
              <BaldachinCard
                b={b}
                mode={mode}
                zoom={zoom}
                editable={editable}
                selected={selectedId === b.id}
                dimmed={dimIds ? dimIds.has(b.id) : false}
                onClick={() => onCardClick?.(b)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
